import React, { useState } from 'react'
import {
  Typography,
  Box,
  Grid,
  useTheme,
  CircularProgress,
  Tooltip,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@material-ui/core'
import { CheckCircle as CheckIcon, Warning, Info, DeleteOutline as DiscardIcon } from '@material-ui/icons'
import { motion, AnimatePresence } from 'framer-motion'
import { describeSpecimen } from '../../lib/specimen'
import { RNA_REFERENCE, describeLibrary, isRnaSelection, pruneSpecimenSource } from '../../lib/rnaLibrary'
import { analysisOptionLabel } from './AnalysisSearchSelect'
import {
  pageVariants,
  ReviewContainer,
  ReviewHeader,
  ReviewFormContainer,
  FormSectionTitle,
  FormRow,
  FormLabel,
  FormValue,
  FormChipContainer,
  FormFileCard,
  FormFileHeader,
  FormFileBadge,
  FormFileName,
  FormFileDescription,
  ClinicalTextBox,
  ClinicalTextLabel,
  ClinicalTextContent,
  FormDivider,
  ReviewChip,
  ValidationSection,
  ValidationHeader,
  ValidationList,
  ValidationItem,
  SubmitSection,
  SubmitButton,
  DiscardButton,
  ButtonContainer,
  ConfirmationOverlay,
  ConfirmationDialog,
  ConfirmationHeader,
  ConfirmationTitle,
  ConfirmationMessage,
  ConfirmationActions,
  ConfirmationButton,
  // New tooltip and HPO styled components
  TooltipContainer,
  TooltipHeader,
  TooltipTitle,
  TooltipSubtitle,
  TooltipText,
  TooltipDescription,
  HPOTermContainer,
  HPOTermId,
  HPOCategoryBadge,
  HPOCategoryText,
  HPOObsoleteBadge,
  HPOObsoleteText,
  SynonymsSection,
  SynonymsLabel,
  SynonymChip,
  SynonymText,
  MoreChip,
  MoreText,
  AdditionalInfoSection,
  InfoNote,
  InfoDate,
  HPOFooter,
  HPOFooterText,
  ReplacementNotice,
  ReplacementText,
  // Variants
  submitButtonVariants,
  discardButtonVariants,
  confirmationOverlayVariants,
  confirmationDialogVariants,
  confirmationButtonVariants,
  buttonVariants,
  StyledButton,
} from '../styles/StyledComponents'
import { GENESUS_AUTOMATIC_COHORT } from './StepConfigure'
import { alpha } from '@material-ui/core/styles'

const StepReview = ({
  analysisType,
  selectedFiles,
  files,
  referenceGenome,
  sequencingType,
  captureKit,
  panelName,
  runAI,
  hpos,
  diseases,
  clinicalHistory,
  userGuidance,
  runCnv,
  runSv,
  runMod,
  cnvMethod,
  technologyType,
  selectedCohortId,
  gatkCohorts = [],
  defaultCohort = null,
  exomedepthCohorts = [],
  carrierConfig,
  cohortName,
  onCohortNameChange,
  cohortSelection = [],
  cohortRows = [],
  cohortBedPanel,
  cohortCnvMethod = 'genesus-cnv',
  onCohortCnvMethodChange,
  multisampleSamples = [],
  onSubmit,
  onDiscard,
  isSubmitting = false,
  handleBack,
  bedFiles = [],
  // Somatic only (roadmap R1.2a): the specimen fields and other analyses of the patient.
  specimen = null,
  linkedAnalyses = [],
  // Tumour RNA only (roadmap R4.1): the library.
  library = null,
}) => {
  const theme = useTheme()
  const isRna = analysisType === 'Somatic' && isRnaSelection(selectedFiles)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const [hoveredFile, setHoveredFile] = useState(null)

  const handleDiscardClick = () => {
    setShowConfirmation(true)
  }

  const handleConfirmDiscard = () => {
    setShowConfirmation(false)
    if (onDiscard) {
      onDiscard()
    }
  }

  const handleCancelDiscard = () => {
    setShowConfirmation(false)
  }

  const getFileId = (f) => f.vcf_id || f.fastq_pair_id || f.ont_bam_id || f.pacbio_bam_id

  const getFileName = (fileId) => {
    const file = files.find((f) => getFileId(f) === fileId)
    if (!file) return 'Unknown'
    // Prefer sample name, otherwise use available filename fields
    const candidates = [
      file.sample, // FASTQ/ONT BAM sample (selection tables use this)
      file.sample_name, // alternate sample field
      file.ont_bam_filename, // ONT BAM filename
      file.pacbio_bam_filename, // PacBio BAM filename
      file.vcf_filename, // VCF filename
      file.name, // generic name
      file.original_name, // upload original name
    ]
    const chosen = candidates.find((v) => typeof v === 'string' && v.trim().length > 0)
    return chosen || 'Unknown'
  }

  const getFileDetails = (fileId) => {
    const file = files.find((f) => getFileId(f) === fileId)
    if (!file) return null

    const fileType = file.file_type
    const createdDate = file.uploaded_at

    return {
      name: getFileName(fileId),
      type: fileType,
      size: file.file_size || 'Unknown size',
      created: createdDate,
      description:
        fileType === 'vcf'
          ? 'Variant Call Format file containing genomic variants'
          : fileType === 'ont_bam'
          ? 'ONT BAM file containing long-read sequencing alignments'
          : fileType === 'pacbio_bam'
          ? 'PacBio BAM file containing HiFi long-read sequencing alignments'
          : 'FASTQ file containing raw sequencing reads',
      files:
        fileType === 'ont_bam'
          ? file.ont_bam_filename || file.original_name
          : fileType === 'pacbio_bam'
          ? file.pacbio_bam_filename || file.original_name
          : fileType === 'fastq'
          ? `${file.fastq1_filename || file.fastq_file_1?.name || 'R1'} + ${
              file.fastq2_filename || file.fastq_file_2?.name || 'R2'
            }`
          : file.vcf_filename || file.name || file.original_name,
    }
  }

  const getHPOTermDetails = (term, termIndex = 0) => {
    // First priority: Use full term objects from hpoTerms if available
    if (hpos?.hpoTerms && hpos.hpoTerms[termIndex]) {
      const fullTerm = hpos.hpoTerms[termIndex]

      // Extract synonyms from various possible fields
      const synonyms = []
      if (fullTerm?.synonyms && Array.isArray(fullTerm.synonyms)) {
        synonyms.push(...fullTerm.synonyms)
      }
      if (fullTerm?.synonym && Array.isArray(fullTerm.synonym)) {
        synonyms.push(...fullTerm.synonym)
      }
      if (fullTerm?.alt_ids && Array.isArray(fullTerm.alt_ids)) {
        synonyms.push(...fullTerm.alt_ids)
      }
      if (fullTerm?.alternative_ids && Array.isArray(fullTerm.alternative_ids)) {
        synonyms.push(...fullTerm.alternative_ids)
      }

      return {
        id: fullTerm?.hpo_id || fullTerm?.id || fullTerm?.value || fullTerm?.hpo || 'Unknown',
        name: fullTerm?.name || fullTerm?.label || fullTerm?.term || fullTerm?.text || 'Unknown phenotype',
        description: fullTerm?.description || fullTerm?.def || fullTerm?.definition || fullTerm?.comment || '',
        synonyms: [...new Set(synonyms)], // Remove duplicates
        definition: fullTerm?.definition || fullTerm?.def || null,
        comment: fullTerm?.comment || null,
        category: fullTerm?.category || fullTerm?.namespace || fullTerm?.class || null,
        // Additional fields that might be available
        xrefs: fullTerm?.xrefs || [],
        is_obsolete: fullTerm?.is_obsolete || false,
        replaced_by: fullTerm?.replaced_by || null,
        creation_date: fullTerm?.creation_date || null,
      }
    }

    // Second priority: Handle full term objects passed directly
    if (term && typeof term === 'object') {
      // Extract synonyms from various possible fields
      const synonyms = []
      if (term?.synonyms && Array.isArray(term.synonyms)) {
        synonyms.push(...term.synonyms)
      }
      if (term?.synonym && Array.isArray(term.synonym)) {
        synonyms.push(...term.synonym)
      }
      if (term?.alt_ids && Array.isArray(term.alt_ids)) {
        synonyms.push(...term.alt_ids)
      }
      if (term?.alternative_ids && Array.isArray(term.alternative_ids)) {
        synonyms.push(...term.alternative_ids)
      }

      return {
        id: term?.hpo_id || term?.id || term?.value || term?.hpo || 'Unknown',
        name: term?.name || term?.label || term?.term || term?.text || 'Unknown phenotype',
        description:
          term?.description ||
          term?.def ||
          term?.definition ||
          term?.comment ||
          (term?.name
            ? `${term.name} - A phenotypic abnormality described in the Human Phenotype Ontology`
            : 'Human Phenotype Ontology term describing a phenotypic abnormality'),
        synonyms: [...new Set(synonyms)], // Remove duplicates
        definition: term?.definition || term?.def || term?.description || null,
        comment: term?.comment || null,
        category: term?.category || term?.namespace || term?.class || null,
        // Additional fields that might be available
        xrefs: term?.xrefs || [],
        is_obsolete: term?.is_obsolete || false,
        replaced_by: term?.replaced_by || null,
        creation_date: term?.creation_date || null,
      }
    }

    // Fallback: If it's just a string ID, try to match with hpoNames if available
    if (typeof term === 'string') {
      const hpoName = hpos?.hpoNames?.[termIndex] || term
      return {
        id: term,
        name: hpoName !== term ? hpoName : `HPO:${term}`,
        description: 'Human Phenotype Ontology term describing a phenotypic abnormality',
        synonyms: [],
        definition: null,
        category: null,
        comment: null,
      }
    }

    // Final fallback
    return {
      id: 'Unknown',
      name: 'Unknown phenotype',
      description: 'Human Phenotype Ontology term',
      synonyms: [],
      definition: null,
      category: null,
      comment: null,
    }
  }

  const getDiseaseDetails = (disease) => {
    if (typeof disease === 'string') {
      return {
        name: disease,
        description: 'Genetic disease or condition',
        omim: null,
        inheritance: null,
        synonyms: [],
        genes: [],
        phenotypes: [],
      }
    }

    // Extract additional information that might be available
    const synonyms = []
    if (disease?.synonyms && Array.isArray(disease.synonyms)) {
      synonyms.push(...disease.synonyms)
    }
    if (disease?.alt_names && Array.isArray(disease.alt_names)) {
      synonyms.push(...disease.alt_names)
    }

    const genes = []
    if (disease?.genes && Array.isArray(disease.genes)) {
      genes.push(...disease.genes)
    }
    if (disease?.associated_genes && Array.isArray(disease.associated_genes)) {
      genes.push(...disease.associated_genes)
    }

    return {
      name: disease?.name || disease?.label || disease?.disease || disease?.title || 'Unknown disease',
      description: disease?.description || disease?.definition || 'Genetic disease or medical condition',
      omim: disease?.omim || disease?.omim_id || null,
      inheritance: disease?.inheritance || disease?.inheritance_pattern || null,
      synonyms: [...new Set(synonyms)], // Remove duplicates
      genes: [...new Set(genes)], // Remove duplicates
      phenotypes: disease?.phenotypes || disease?.hpo_terms || [],
      category: disease?.category || disease?.disease_class || null,
      prevalence: disease?.prevalence || null,
    }
  }

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const validateConfiguration = () => {
    const errors = []
    const warnings = []

    // Multiple (cohort) mode validation
    if (analysisType === 'Multiple') {
      const selectedCount = (cohortRows && cohortRows.length) || (cohortSelection && cohortSelection.length) || 0
      if (!cohortName || !cohortName.trim()) {
        errors.push('Cohort name is required')
      }
      if (selectedCount === 0) {
        errors.push('At least one FASTQ pair must be selected')
      }
      return { errors, warnings }
    }

    // Multisample mode validation
    if (analysisType === 'Multisample') {
      const validSamples = multisampleSamples.filter((s) => s.fileId && s.relation)
      if (validSamples.length < 2) {
        errors.push('At least two samples with file and relation are required')
      }
      return { errors, warnings }
    }

    // Determine file type
    const primaryFile = files.find((f) => getFileId(f) === selectedFiles.primary)
    const isVCFFile = !!primaryFile?.vcf_id
    const isOntBamFile = !!primaryFile?.ont_bam_id
    const isPacbioBamFile = !!primaryFile?.pacbio_bam_id
    const isFASTQFile = !isVCFFile && !isOntBamFile && !isPacbioBamFile

    // Required field validations
    if (!analysisType) errors.push('Analysis type is required')
    if (!selectedFiles.primary) errors.push('Primary file selection is required')
    if (analysisType === 'Somatic') {
      const somaticType = selectedFiles.somaticAnalysisType
      if (somaticType === 'tumor-normal' && !selectedFiles.normal) {
        errors.push('Normal file is required for tumor/normal somatic analysis')
      }
    }
    if (analysisType === 'Carrier') {
      if (!carrierConfig) errors.push('Carrier screening configuration is required')
      if (carrierConfig) {
        const selectedCount = Object.values(selectedFiles).filter(Boolean).length
        if (selectedCount < carrierConfig.samples_required) {
          errors.push(`${carrierConfig.samples_required} samples are required for the selected carrier screening panel`)
        }
      }
    }

    // File type specific validations (skip for carrier and ONT BAM analysis)
    if (analysisType !== 'Carrier' && !isOntBamFile && !isPacbioBamFile) {
      if (isVCFFile) {
        // VCF files require reference genome
        if (!referenceGenome) errors.push('Reference genome must be selected')
      }

      if (isFASTQFile && !isRna) {
        // FASTQ files require sequencing type (an RNA library has none)
        if (!sequencingType) errors.push('Sequencing type must be selected')
      }
      if (isRna && !library?.libraryPrep) errors.push('RNA library preparation must be selected')
    }

    // Conditional validations (skip for carrier, ONT BAM, and PacBio BAM analysis)
    if (analysisType !== 'Carrier' && !isOntBamFile && !isPacbioBamFile) {
      if (sequencingType === 'Whole Exome' && !captureKit) {
        warnings.push('Capture kit information is recommended for exome analysis')
      }
      if (sequencingType === 'Targeted Panel' && !panelName) {
        warnings.push('Panel name should be specified for targeted sequencing')
      }

      // AI-related validations
      if (runAI) {
        if (!hpos?.hpos?.length) warnings.push('HPO terms help improve AI analysis accuracy')
        if (!clinicalHistory?.trim()) warnings.push('Clinical history provides important context for AI analysis')
      }
    }

    return { errors, warnings }
  }

  const { errors, warnings } = validateConfiguration()
  const canSubmit = errors.length === 0 && !isSubmitting

  const handleSubmit = () => {
    if (canSubmit && onSubmit) {
      onSubmit()
    }
  }

  const renderSubmitSection = () => (
    <motion.div custom={5} variants={submitButtonVariants} initial="hidden" animate="visible">
      <SubmitSection>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 700,
              mb: 1,
              fontSize: '1.3rem',
            }}
          >
            {isSubmitting ? 'Submitting Analysis...' : 'Ready to Start Analysis'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '1rem',
              lineHeight: 1.6,
            }}
          >
            {isSubmitting
              ? 'Please wait while we process your submission.'
              : canSubmit
              ? 'Your configuration has been validated and is ready for processing.'
              : 'Please resolve the configuration errors above before proceeding.'}
          </Typography>
        </Box>

        <ButtonContainer>
          {/* Back Button */}
          <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
            <StyledButton variant="outlined" onClick={handleBack}>
              Back
            </StyledButton>
          </motion.div>

          {/* Discard Button */}
          <motion.div
            variants={discardButtonVariants}
            initial="idle"
            whileHover={!isSubmitting ? 'hover' : 'idle'}
            whileTap={!isSubmitting ? 'tap' : 'idle'}
          >
            <DiscardButton
              variant="outlined"
              size="large"
              onClick={handleDiscardClick}
              disabled={isSubmitting}
              startIcon={<DiscardIcon />}
              sx={{
                opacity: isSubmitting ? 0.5 : 1,
                pointerEvents: isSubmitting ? 'none' : 'auto',
              }}
            >
              Discard
            </DiscardButton>
          </motion.div>

          {/* Submit Button */}
          <motion.div
            variants={submitButtonVariants}
            initial="idle"
            whileHover={canSubmit ? 'hover' : 'idle'}
            whileTap={canSubmit ? 'tap' : 'idle'}
            animate={isSubmitting ? 'loading' : 'idle'}
          >
            <Box sx={{ position: 'relative' }}>
              <SubmitButton
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!canSubmit}
                canSubmit={canSubmit && !isSubmitting}
                startIcon={isSubmitting ? null : <CheckIcon />}
                sx={{
                  opacity: isSubmitting ? 0.8 : 1,
                  pointerEvents: isSubmitting ? 'none' : 'auto',
                }}
              >
                {isSubmitting ? 'Processing...' : 'Start Analysis'}
              </SubmitButton>

              {isSubmitting && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                    color: theme.palette.primary.contrastText,
                  }}
                />
              )}
            </Box>
          </motion.div>
        </ButtonContainer>

        {!canSubmit && !isSubmitting && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.error.main,
                fontSize: '0.85rem',
                fontWeight: 500,
              }}
            >
              Fix configuration errors to proceed
            </Typography>
          </Box>
        )}
      </SubmitSection>
    </motion.div>
  )

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      <ReviewContainer>
        {/* Unified Form Container */}
        <ReviewFormContainer>
          {/* Analysis Configuration Section */}
          <FormSectionTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Analysis Configuration</Box>
          </FormSectionTitle>

          <FormRow>
            <Tooltip title="Type of genomic analysis to be performed on the selected samples" placement="top" arrow>
              <FormLabel sx={{ cursor: 'help' }}>Analysis Type</FormLabel>
            </Tooltip>
            <FormValue>
              {analysisType === 'Somatic' && selectedFiles.somaticAnalysisType
                ? `${analysisType} (${
                    isRna
                      ? 'Tumour RNA, tumour-only'
                      : selectedFiles.somaticAnalysisType === 'tumor-only'
                      ? 'Tumor-Only'
                      : 'Tumor/Normal'
                  })`
                : analysisType === 'Carrier'
                ? `${analysisType} Screening`
                : analysisType === 'Multiple'
                ? `Cohort Creation`
                : analysisType === 'Multisample'
                ? `Multisample Analysis`
                : analysisType}
            </FormValue>
          </FormRow>

          {analysisType === 'Somatic' && (
            <>
              {isRna && (
                <FormRow>
                  <Tooltip
                    title="The RNA library. A blank strandedness is inferred from the reads; other blanks read not recorded."
                    placement="top"
                    arrow
                  >
                    <FormLabel sx={{ cursor: 'help' }}>RNA Library</FormLabel>
                  </Tooltip>
                  <FormValue data-testid="review-rna-library">{describeLibrary(library || {})}</FormValue>
                </FormRow>
              )}
              <FormRow>
                <Tooltip
                  title="What the lab recorded about the specimen. Blank fields are shown as not recorded on the analysis page."
                  placement="top"
                  arrow
                >
                  <FormLabel sx={{ cursor: 'help' }}>Tumour Specimen</FormLabel>
                </Tooltip>
                <FormValue>
                  {isRna && pruneSpecimenSource(library, linkedAnalyses)?.specimenFromAnalysisId
                    ? `Shared with analysis ${library.specimenFromAnalysisId} (same tumour block)`
                    : describeSpecimen({ specimenType: specimen?.tumourType }, { tumour: false })}
                </FormValue>
              </FormRow>
              {selectedFiles.somaticAnalysisType === 'tumor-normal' && (
                <FormRow>
                  <FormLabel>Normal Specimen</FormLabel>
                  <FormValue>{describeSpecimen({ specimenType: specimen?.normalType }, { tumour: false })}</FormValue>
                </FormRow>
              )}
              <FormRow>
                <Tooltip
                  title="Linked as the same patient, unverified: shown on the analysis page, never used as evidence until an identity check confirms them."
                  placement="top"
                  arrow
                >
                  <FormLabel sx={{ cursor: 'help' }}>Same Patient</FormLabel>
                </Tooltip>
                <FormValue>
                  {linkedAnalyses.length
                    ? linkedAnalyses.map((a) => analysisOptionLabel(a)).join('; ')
                    : selectedFiles.somaticAnalysisType === 'tumor-normal'
                    ? 'Analyses of the matched normal upload (linked automatically)'
                    : 'None named'}
                </FormValue>
              </FormRow>
            </>
          )}

          {technologyType === 'ont-long-read' && (
            <FormRow>
              <Tooltip title="Sequencing technology used to generate the data" placement="top" arrow>
                <FormLabel sx={{ cursor: 'help' }}>Technology</FormLabel>
              </Tooltip>
              <FormValue>ONT Long-Read Sequencing</FormValue>
            </FormRow>
          )}

          {technologyType === 'ont-long-read' && (
            <>
              <FormRow>
                <Tooltip title="Copy Number Variation detection using Spectre" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>CNV Analysis</FormLabel>
                </Tooltip>
                <FormChipContainer>
                  <ReviewChip
                    label={runCnv ? 'Enabled: Spectre' : 'Disabled'}
                    chipColor={runCnv ? 'success' : 'default'}
                    size="small"
                  />
                </FormChipContainer>
              </FormRow>
              <FormRow>
                <Tooltip title="Structural Variant detection using Sniffles2" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>SV Analysis</FormLabel>
                </Tooltip>
                <FormChipContainer>
                  <ReviewChip
                    label={runSv ? 'Enabled: Sniffles2' : 'Disabled'}
                    chipColor={runSv ? 'success' : 'default'}
                    size="small"
                  />
                </FormChipContainer>
              </FormRow>
              <FormRow>
                <Tooltip title="CpG methylation (5mC) detection using modkit" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>Methylation</FormLabel>
                </Tooltip>
                <FormChipContainer>
                  <ReviewChip
                    label={runMod ? 'Enabled: modkit' : 'Disabled'}
                    chipColor={runMod ? 'success' : 'default'}
                    size="small"
                  />
                </FormChipContainer>
              </FormRow>
            </>
          )}

          {technologyType === 'pacbio-long-read' && (
            <FormRow>
              <Tooltip title="Sequencing technology used to generate the data" placement="top" arrow>
                <FormLabel sx={{ cursor: 'help' }}>Technology</FormLabel>
              </Tooltip>
              <FormValue>PacBio HiFi Long-Read Sequencing</FormValue>
            </FormRow>
          )}

          {technologyType === 'pacbio-long-read' && (
            <>
              <FormRow>
                <Tooltip title="Copy Number Variant detection using HiFiCNV" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>CNV Analysis</FormLabel>
                </Tooltip>
                <FormChipContainer>
                  <ReviewChip
                    label={runCnv ? 'Enabled: HiFiCNV' : 'Disabled'}
                    chipColor={runCnv ? 'success' : 'default'}
                    size="small"
                  />
                </FormChipContainer>
              </FormRow>
              <FormRow>
                <Tooltip title="Structural Variant detection using pbsv" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>SV Analysis</FormLabel>
                </Tooltip>
                <FormChipContainer>
                  <ReviewChip
                    label={runSv ? 'Enabled: pbsv' : 'Disabled'}
                    chipColor={runSv ? 'success' : 'default'}
                    size="small"
                  />
                </FormChipContainer>
              </FormRow>
              <FormRow>
                <Tooltip title="CpG methylation (5mC) detection using pb-CpG-tools" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>Methylation Analysis</FormLabel>
                </Tooltip>
                <FormChipContainer>
                  <ReviewChip
                    label={runMod ? 'Enabled: pb-CpG-tools' : 'Disabled'}
                    chipColor={runMod ? 'success' : 'default'}
                    size="small"
                  />
                </FormChipContainer>
              </FormRow>
            </>
          )}

          {analysisType === 'Carrier' && carrierConfig && (
            <>
              <FormRow>
                <Tooltip title="Selected carrier screening panel configuration" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>Screening Panel</FormLabel>
                </Tooltip>
                <FormValue>{carrierConfig.panel_title}</FormValue>
              </FormRow>

              <FormRow>
                <Tooltip title="Number of samples required for this carrier screening panel" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>Samples Required</FormLabel>
                </Tooltip>
                <FormValue>{carrierConfig.samples_required}</FormValue>
              </FormRow>
            </>
          )}

          {isRna ? (
            <FormRow>
              <FormLabel>Reference Genome</FormLabel>
              <FormValue>{RNA_REFERENCE} (GENCODE v36 RNA bundle)</FormValue>
            </FormRow>
          ) : (
            referenceGenome && (
              <FormRow>
                <Tooltip
                  title="Human genome reference version used for alignment and variant calling"
                  placement="top"
                  arrow
                >
                  <FormLabel sx={{ cursor: 'help' }}>Reference Genome</FormLabel>
                </Tooltip>
                <FormValue>{referenceGenome}</FormValue>
              </FormRow>
            )
          )}

          {sequencingType && (
            <FormRow>
              <Tooltip title="Type of sequencing method used to generate the genomic data" placement="top" arrow>
                <FormLabel sx={{ cursor: 'help' }}>Sequencing Type</FormLabel>
              </Tooltip>
              <FormValue>{sequencingType}</FormValue>
            </FormRow>
          )}

          {captureKit && (
            <FormRow>
              <Tooltip
                title="Capture kit used for targeted sequencing to define genomic regions of interest"
                placement="top"
                arrow
              >
                <FormLabel sx={{ cursor: 'help' }}>Capture Kit</FormLabel>
              </Tooltip>
              <FormValue>
                {(() => {
                  const bedFile = bedFiles?.find((f) => f.name === captureKit)
                  return bedFile?.original_name || captureKit
                })()}
              </FormValue>
            </FormRow>
          )}

          {panelName && (
            <FormRow>
              <Tooltip title="Gene panel used for targeted analysis focusing on specific genes" placement="top" arrow>
                <FormLabel sx={{ cursor: 'help' }}>Panel Name</FormLabel>
              </Tooltip>
              <FormValue>{panelName}</FormValue>
            </FormRow>
          )}

          {captureKit && (
            <FormRow>
              <Tooltip
                title="Copy Number Variation analysis for detecting structural genomic changes"
                placement="top"
                arrow
              >
                <FormLabel sx={{ cursor: 'help' }}>CNV Analysis</FormLabel>
              </Tooltip>
              <FormValue>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={runCnv ? 'Enabled' : 'Disabled'}
                    size="small"
                    sx={{
                      transition: 'all 0.2s ease',
                      boxShadow: runCnv ? `0 2px 8px ${alpha(theme.palette.success.main, 0.19)}` : undefined,
                    }}
                  />
                  {runCnv && cnvMethod && (
                    <Chip
                      label={`Method: ${
                        cnvMethod === 'ecole'
                          ? 'Ecole'
                          : cnvMethod === 'genesus-cnv'
                          ? 'Genesus CNV'
                          : cnvMethod === 'exomedepth'
                          ? 'ExomeDepth Cohort'
                          : 'ERROR'
                      }`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                  )}
                  {runCnv && cnvMethod === 'genesus-cnv' && (
                    <Chip
                      label={`Cohort: ${(() => {
                        // Sentinel = GENESUS Automatic (on-demand pipeline)
                        if (!selectedCohortId || selectedCohortId === GENESUS_AUTOMATIC_COHORT) {
                          return 'GENESUS Automatic (on-demand)'
                        }
                        // Check default cohort id (legacy compatibility — should no longer hit)
                        if (defaultCohort && defaultCohort.id === selectedCohortId) {
                          return 'GENESUS Automatic'
                        }
                        // Then check user's cohorts
                        const userCohort = gatkCohorts.find((c) => c.id === selectedCohortId)
                        return userCohort?.cohort_name || 'Unknown'
                      })()}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                  )}
                  {runCnv && cnvMethod === 'exomedepth' && selectedCohortId && (
                    <Chip
                      label={`Cohort: ${
                        exomedepthCohorts.find((c) => c.id === selectedCohortId)?.cohort_name || 'Unknown'
                      }`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                  )}
                </Box>
              </FormValue>
            </FormRow>
          )}

          {selectedFiles?.isDemultiplexed && (
            <FormRow>
              <Tooltip title="Demultiplexed file used for analysis" placement="top" arrow>
                <FormLabel sx={{ cursor: 'help' }}>Demultiplexed File</FormLabel>
              </Tooltip>
              <FormValue>Yes</FormValue>
            </FormRow>
          )}

          <FormDivider />

          {analysisType === 'Multisample' ? (
            <>
              <FormSectionTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Sample Configuration</Box>
              </FormSectionTitle>
              <FormRow>
                <FormLabel>Total Samples</FormLabel>
                <FormValue>{multisampleSamples.filter((s) => s.fileId).length}</FormValue>
              </FormRow>
              {multisampleSamples
                .filter((s) => s.fileId)
                .map((sample, index) => (
                  <FormRow key={index}>
                    <FormLabel>{sample.relation || `Sample ${index + 1}`}</FormLabel>
                    <FormValue>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Chip
                          label={sample.include ? 'Include' : 'Exclude'}
                          size="small"
                          color={sample.include ? 'primary' : 'secondary'}
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                        <Chip
                          label={`GT: ${sample.gt || 'any'}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                        <Chip
                          label={sample.selectedOption?.label || sample.fileId}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.75rem', maxWidth: 200 }}
                        />
                      </Box>
                    </FormValue>
                  </FormRow>
                ))}
            </>
          ) : analysisType === 'Multiple' ? (
            <>
              <FormSectionTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Cohort Details</Box>
              </FormSectionTitle>
              <FormRow>
                <Tooltip title="Name of this cohort analysis" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>Cohort Name</FormLabel>
                </Tooltip>
                <FormValue>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <input
                      value={cohortName || ''}
                      onChange={(e) => onCohortNameChange && onCohortNameChange(e.target.value)}
                      placeholder="Enter cohort name"
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: `1px solid ${theme.palette.divider}`,
                        background: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        fontSize: '0.95rem',
                      }}
                    />
                  </Box>
                </FormValue>
              </FormRow>
              <FormRow>
                <Tooltip title="Bed panel used for targeted sequencing" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>Bed Panel</FormLabel>
                </Tooltip>
                <FormValue>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(() => {
                        const bedFile = bedFiles?.find((f) => f.name === cohortBedPanel)
                        return bedFile?.original_name || cohortBedPanel || 'Not selected'
                      })()}
                    </Typography>
                  </Box>
                </FormValue>
              </FormRow>
              <FormRow>
                <Tooltip title="Select the CNV analysis method for this cohort" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>CNV Method</FormLabel>
                </Tooltip>
                <FormValue>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={cohortCnvMethod}
                      onChange={(e) => onCohortCnvMethodChange && onCohortCnvMethodChange(e.target.value)}
                    >
                      <FormControlLabel
                        value="genesus-cnv"
                        control={<Radio size="small" color="primary" />}
                        label={<Typography variant="body2">GENESUS</Typography>}
                      />
                      <FormControlLabel
                        value="exomedepth"
                        control={<Radio size="small" color="primary" />}
                        label={<Typography variant="body2">ExomeDepth</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                </FormValue>
              </FormRow>
              <FormRow>
                <Tooltip title="Selected FASTQ pairs included in this cohort" placement="top" arrow>
                  <FormLabel sx={{ cursor: 'help' }}>Selected Files</FormLabel>
                </Tooltip>
                <FormValue>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cohortRows?.length || 0} selected
                    </Typography>
                    <FormChipContainer>
                      {(cohortRows || []).slice(0, 18).map((row, idx) => (
                        <ReviewChip
                          key={row.id || idx}
                          label={
                            row.sample_name ||
                            row.fastq1_filename ||
                            row.fastq_file_1?.original_name ||
                            `Item ${idx + 1}`
                          }
                          chipColor="default"
                          size="small"
                        />
                      ))}
                      {cohortRows && cohortRows.length > 18 && (
                        <ReviewChip label={`+${cohortRows.length - 18} more`} chipColor="default" size="small" />
                      )}
                    </FormChipContainer>
                  </Box>
                </FormValue>
              </FormRow>
            </>
          ) : (
            <>
              {/* Selected Files Section */}
              <FormSectionTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Selected Files</Box>
              </FormSectionTitle>

              {selectedFiles.primary && !selectedFiles.isDemultiplexed && (
                <Tooltip
                  title={
                    <TooltipContainer maxWidth="300px">
                      {(() => {
                        const details = getFileDetails(selectedFiles.primary)
                        return details ? (
                          <>
                            <TooltipSubtitle>File Details</TooltipSubtitle>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                              <strong>Type:</strong> {details.type.toUpperCase()}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                              <strong>Created:</strong> {details.created}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                              <strong>Files:</strong> {details.files}
                            </Typography>
                            <TooltipDescription isItalic sx={{ mt: 1 }}>
                              {details.description}
                            </TooltipDescription>
                          </>
                        ) : (
                          <TooltipDescription>File details not available</TooltipDescription>
                        )
                      })()}
                    </TooltipContainer>
                  }
                  placement="top"
                  arrow
                >
                  <motion.div
                    whileHover={{ scale: 1.02, y: -2 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <FormFileCard
                      isPrimary={true}
                      onMouseEnter={() => setHoveredFile('primary')}
                      onMouseLeave={() => setHoveredFile(null)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow:
                          hoveredFile === 'primary'
                            ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.19)}`
                            : undefined,
                      }}
                    >
                      <FormFileHeader>
                        <FormFileBadge isPrimary={true}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {analysisType === 'Somatic'
                              ? 'Tumor Sample'
                              : analysisType === 'Carrier'
                              ? 'Maternal Sample'
                              : 'Primary Sample'}
                          </Box>
                        </FormFileBadge>
                      </FormFileHeader>
                      <FormFileName>{getFileName(selectedFiles.primary)}</FormFileName>
                      <FormFileDescription>
                        {analysisType === 'Somatic'
                          ? 'Tumor sample for analysis'
                          : analysisType === 'Carrier'
                          ? 'Maternal sample file'
                          : 'Primary sample file'}
                      </FormFileDescription>
                    </FormFileCard>
                  </motion.div>
                </Tooltip>
              )}

              {selectedFiles.normal && (
                <Tooltip
                  title={
                    <TooltipContainer maxWidth="300px">
                      {(() => {
                        const details = getFileDetails(selectedFiles.normal)
                        return details ? (
                          <>
                            <TooltipSubtitle>File Details</TooltipSubtitle>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                              <strong>Type:</strong> {details.type}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                              <strong>Created:</strong> {details.created}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                              <strong>Files:</strong> {details.files}
                            </Typography>
                            <TooltipDescription isItalic sx={{ mt: 1 }}>
                              {details.description}
                            </TooltipDescription>
                          </>
                        ) : (
                          <TooltipDescription>File details not available</TooltipDescription>
                        )
                      })()}
                    </TooltipContainer>
                  }
                  placement="top"
                  arrow
                >
                  <motion.div
                    whileHover={{ scale: 1.02, y: -2 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <FormFileCard
                      isPrimary={false}
                      onMouseEnter={() => setHoveredFile('normal')}
                      onMouseLeave={() => setHoveredFile(null)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow:
                          hoveredFile === 'normal'
                            ? `0 6px 20px ${alpha(theme.palette.secondary.main, 0.19)}`
                            : undefined,
                      }}
                    >
                      <FormFileHeader>
                        <FormFileBadge isPrimary={false}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {analysisType === 'Carrier' ? 'Paternal Sample' : 'Normal Sample'}
                          </Box>
                        </FormFileBadge>
                      </FormFileHeader>
                      <FormFileName>{getFileName(selectedFiles.normal)}</FormFileName>
                      <FormFileDescription>
                        {analysisType === 'Carrier' ? 'Paternal sample file' : 'Control sample for comparison'}
                      </FormFileDescription>
                    </FormFileCard>
                  </motion.div>
                </Tooltip>
              )}

              {/* Additional Generic Samples */}
              {selectedFiles.isDemultiplexed &&
                Object.keys(selectedFiles)
                  .filter((key) => (key.startsWith('sample') || key.startsWith('primary')) && selectedFiles[key])
                  .sort((a, b) => {
                    // Sort numerically if possible (sample3, sample4, etc)
                    const numA = parseInt(a.replace('sample', ''), 10)
                    const numB = parseInt(b.replace('sample', ''), 10)
                    return numA - numB
                  })
                  .map((key) => {
                    const fileId = selectedFiles[key]
                    const sampleNum = key.replace('sample', '')
                    return (
                      <Tooltip
                        key={key}
                        title={
                          <TooltipContainer maxWidth="300px">
                            {(() => {
                              const details = getFileDetails(fileId)
                              return details ? (
                                <>
                                  <TooltipSubtitle>File Details</TooltipSubtitle>
                                  <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                    <strong>Type:</strong> {details.type}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                    <strong>Created:</strong> {details.created}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                    <strong>Files:</strong> {details.files}
                                  </Typography>
                                  <TooltipDescription isItalic sx={{ mt: 1 }}>
                                    {details.description}
                                  </TooltipDescription>
                                </>
                              ) : (
                                <TooltipDescription>File details not available</TooltipDescription>
                              )
                            })()}
                          </TooltipContainer>
                        }
                        placement="top"
                        arrow
                      >
                        <motion.div
                          whileHover={{ scale: 1.02, y: -2 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          style={{ marginTop: '16px' }} // Add some spacing between cards
                        >
                          <FormFileCard
                            isPrimary={false}
                            onMouseEnter={() => setHoveredFile(key)}
                            onMouseLeave={() => setHoveredFile(null)}
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              boxShadow:
                                hoveredFile === key
                                  ? `0 6px 20px ${alpha(theme.palette.secondary.main, 0.19)}`
                                  : undefined,
                            }}
                          >
                            <FormFileHeader>
                              <FormFileBadge isPrimary={false} sx={{ background: theme.palette.info.dark }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Sample {sampleNum}</Box>
                              </FormFileBadge>
                            </FormFileHeader>
                            <FormFileName>{getFileName(fileId)}</FormFileName>
                            <FormFileDescription>Indexes/Barcodes for the sample</FormFileDescription>
                          </FormFileCard>
                        </motion.div>
                      </Tooltip>
                    )
                  })}
            </>
          )}

          <FormDivider />

          {/* Clinical Data & AI Section */}
          {analysisType !== 'Multiple' && (
            <>
              <FormSectionTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Clinical Data & AI Configuration</Box>
              </FormSectionTitle>

              <FormRow>
                <Tooltip
                  title={
                    runAI
                      ? 'AI-powered variant prioritization and clinical interpretation is enabled'
                      : 'Standard analysis without AI assistance'
                  }
                  placement="top"
                  arrow
                >
                  <FormLabel sx={{ cursor: 'help' }}>AI Prioritization</FormLabel>
                </Tooltip>
                <FormChipContainer>
                  <motion.div whileHover={{ scale: 1.05 }} transition={{ type: 'spring', stiffness: 400, damping: 20 }}>
                    <ReviewChip
                      label={runAI ? 'Enabled' : 'Disabled'}
                      chipColor={runAI ? 'success' : 'default'}
                      size="small"
                      sx={{
                        transition: 'all 0.2s ease',
                        boxShadow: runAI ? `0 2px 8px ${alpha(theme.palette.success.main, 0.19)}` : undefined,
                      }}
                    />
                  </motion.div>
                </FormChipContainer>
              </FormRow>

              {analysisType !== 'Multiple' && runAI && (
                <>
                  {/* HPO Terms */}
                  {hpos?.hpos?.length > 0 && (
                    <FormRow>
                      <FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            HPO Phenotype Terms ({hpos.hpos.length})
                          </Typography>
                        </Box>
                      </FormLabel>
                      <FormChipContainer>
                        {hpos.hpos.slice(0, 4).map((term, index) => {
                          const termDetails = getHPOTermDetails(term, index)
                          return (
                            <Tooltip
                              key={index}
                              title={
                                <HPOTermContainer>
                                  {/* Header with HPO ID and status */}
                                  <TooltipHeader>
                                    <Box>
                                      <HPOTermId>{termDetails.id}</HPOTermId>
                                      {termDetails.category && (
                                        <HPOCategoryBadge>
                                          <HPOCategoryText>{termDetails.category}</HPOCategoryText>
                                        </HPOCategoryBadge>
                                      )}
                                    </Box>
                                    {termDetails.is_obsolete && (
                                      <HPOObsoleteBadge>
                                        <HPOObsoleteText>OBSOLETE</HPOObsoleteText>
                                      </HPOObsoleteBadge>
                                    )}
                                  </TooltipHeader>

                                  {/* Term Name */}
                                  <TooltipTitle>{termDetails.name}</TooltipTitle>

                                  {/* Description */}
                                  {termDetails.description && termDetails.description !== termDetails.name && (
                                    <Box sx={{ mb: 2 }}>
                                      <TooltipText
                                        isItalic={termDetails.description.includes('phenotypic abnormality')}
                                      >
                                        {termDetails.description}
                                      </TooltipText>
                                    </Box>
                                  )}

                                  {/* Synonyms Section */}
                                  {termDetails.synonyms.length > 0 && (
                                    <SynonymsSection>
                                      <SynonymsLabel>Synonyms:</SynonymsLabel>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {termDetails.synonyms.slice(0, 5).map((synonym, synIndex) => (
                                          <SynonymChip key={synIndex}>
                                            <SynonymText>{synonym}</SynonymText>
                                          </SynonymChip>
                                        ))}
                                        {termDetails.synonyms.length > 5 && (
                                          <MoreChip>
                                            <MoreText>+{termDetails.synonyms.length - 5} more</MoreText>
                                          </MoreChip>
                                        )}
                                      </Box>
                                    </SynonymsSection>
                                  )}

                                  {/* Additional Information */}
                                  {(termDetails.xrefs?.length > 0 ||
                                    termDetails.comment ||
                                    termDetails.creation_date) && (
                                    <AdditionalInfoSection>
                                      {termDetails.comment && termDetails.comment !== termDetails.description && (
                                        <InfoNote>Note: {termDetails.comment}</InfoNote>
                                      )}
                                      {termDetails.creation_date && (
                                        <InfoDate>
                                          Created: {new Date(termDetails.creation_date).toLocaleDateString()}
                                        </InfoDate>
                                      )}
                                    </AdditionalInfoSection>
                                  )}

                                  {/* Footer with HPO info */}
                                  <HPOFooter>
                                    <HPOFooterText>Human Phenotype Ontology (HPO) Term</HPOFooterText>
                                  </HPOFooter>

                                  {/* Replacement notice for obsolete terms */}
                                  {termDetails.is_obsolete && termDetails.replaced_by && (
                                    <ReplacementNotice>
                                      <ReplacementText>Replaced by: {termDetails.replaced_by}</ReplacementText>
                                    </ReplacementNotice>
                                  )}
                                </HPOTermContainer>
                              }
                              placement="top"
                              arrow
                            >
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                              >
                                <ReviewChip
                                  label={
                                    termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:')
                                      ? termDetails.id
                                      : termDetails.name.length > 20
                                      ? `${termDetails.name.substring(0, 20)}...`
                                      : termDetails.name
                                  }
                                  chipColor="primary"
                                  size="small"
                                  sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    fontFamily:
                                      termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:')
                                        ? 'fontFamilyMono'
                                        : 'inherit',
                                    fontSize:
                                      termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:')
                                        ? '0.75rem'
                                        : '0.8rem',
                                    fontWeight:
                                      termDetails.id && termDetails.id !== 'Unknown' && termDetails.id.startsWith('HP:')
                                        ? 600
                                        : 500,
                                    maxWidth: '150px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    },
                                    '&:hover': {
                                      backgroundColor: `${alpha(theme.palette.primary.main, 0.13)}`,
                                      boxShadow: `0 3px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                                      transform: 'translateY(-1px)',
                                    },
                                  }}
                                />
                              </motion.div>
                            </Tooltip>
                          )
                        })}
                        {hpos.hpos.length > 4 && (
                          <Tooltip
                            title={
                              <Box sx={{ p: 1.5, maxWidth: '450px' }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 700,
                                    mb: 2,
                                    color: 'white',
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  Additional HPO Terms ({hpos.hpos.length - 4})
                                </Typography>
                                <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                                  {hpos.hpos.slice(4).map((term, index) => {
                                    const termDetails = getHPOTermDetails(term, index + 4)
                                    return (
                                      <Box
                                        key={index}
                                        sx={{
                                          mb: 2,
                                          pb: 2,
                                          borderBottom:
                                            index < hpos.hpos.slice(4).length - 1
                                              ? '1px solid rgba(255, 255, 255, 0.08)'
                                              : 'none',
                                        }}
                                      >
                                        {/* Header with HPO ID and category */}
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: theme.palette.primary.light,
                                              fontWeight: 700,
                                              fontSize: '0.75rem',
                                              fontFamily: 'fontFamilyMono',
                                              letterSpacing: '0.5px',
                                            }}
                                          >
                                            {termDetails.id}
                                          </Typography>
                                          {termDetails.category && (
                                            <Box
                                              sx={{
                                                px: 0.8,
                                                py: 0.2,
                                                backgroundColor: alpha(theme.palette.info.main, 0.08),
                                                borderRadius: '6px',
                                                border: `1px solid ${alpha(theme.palette.info.main, 0.19)}`,
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: theme.palette.info.light,
                                                  fontWeight: 600,
                                                  fontSize: '0.75rem',
                                                  textTransform: 'uppercase',
                                                }}
                                              >
                                                {termDetails.category}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>

                                        {/* Term Name */}
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 700,
                                            color: 'white',
                                            mb: 1,
                                            fontSize: '0.9rem',
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          {termDetails.name}
                                        </Typography>

                                        {/* Description */}
                                        {termDetails.description && termDetails.description !== termDetails.name && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: 'rgba(255, 255, 255, 0.75)',
                                              fontSize: '0.75rem',
                                              lineHeight: 1.4,
                                              display: 'block',
                                              mb: 1,
                                              fontStyle: termDetails.description.includes('phenotypic abnormality')
                                                ? 'italic'
                                                : 'normal',
                                            }}
                                          >
                                            {termDetails.description.length > 120
                                              ? `${termDetails.description.substring(0, 120)}...`
                                              : termDetails.description}
                                          </Typography>
                                        )}

                                        {/* Synonyms preview */}
                                        {termDetails.synonyms.length > 0 && (
                                          <Box sx={{ mt: 1 }}>
                                            <Box
                                              sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: 'rgba(255, 255, 255, 0.6)',
                                                  fontSize: '0.75rem',
                                                  fontWeight: 600,
                                                  textTransform: 'uppercase',
                                                  letterSpacing: '0.3px',
                                                }}
                                              >
                                                Synonyms:
                                              </Typography>
                                              {termDetails.synonyms.slice(0, 2).map((synonym, synIndex) => (
                                                <Box
                                                  key={synIndex}
                                                  sx={{
                                                    px: 0.8,
                                                    py: 0.2,
                                                    backgroundColor: alpha(theme.palette.success.main, 0.13),
                                                    borderRadius: '8px',
                                                    border: `1px solid ${alpha(theme.palette.success.main, 0.21)}`,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: theme.palette.success.light,
                                                      fontSize: '0.75rem',
                                                      fontWeight: 500,
                                                    }}
                                                  >
                                                    {synonym.length > 25 ? `${synonym.substring(0, 25)}...` : synonym}
                                                  </Typography>
                                                </Box>
                                              ))}
                                              {termDetails.synonyms.length > 2 && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: theme.palette.success.light,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    fontStyle: 'italic',
                                                  }}
                                                >
                                                  +{termDetails.synonyms.length - 2} more
                                                </Typography>
                                              )}
                                            </Box>
                                          </Box>
                                        )}

                                        {/* Status indicators */}
                                        {termDetails.is_obsolete && (
                                          <Box sx={{ mt: 1 }}>
                                            <Box
                                              sx={{
                                                px: 1,
                                                py: 0.3,
                                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                                                borderRadius: '6px',
                                                border: `1px solid ${alpha(theme.palette.error.main, 0.19)}`,
                                                display: 'inline-block',
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: theme.palette.error.light,
                                                  fontWeight: 600,
                                                  fontSize: '0.75rem',
                                                }}
                                              >
                                                OBSOLETE TERM
                                              </Typography>
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    )
                                  })}
                                </Box>
                              </Box>
                            }
                            placement="top"
                            arrow
                          >
                            <ReviewChip
                              label={`+${hpos.hpos.length - 4} more`}
                              chipColor="default"
                              size="small"
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )}
                      </FormChipContainer>
                    </FormRow>
                  )}

                  {/* Diseases */}
                  {diseases?.length > 0 && (
                    <FormRow>
                      <FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Diseases ({diseases.length})</Box>
                      </FormLabel>
                      <FormChipContainer>
                        {diseases.slice(0, 3).map((disease, index) => {
                          const diseaseDetails = getDiseaseDetails(disease)
                          return (
                            <Tooltip
                              key={index}
                              title={
                                <Box sx={{ p: 1.5, maxWidth: '400px' }}>
                                  {/* Disease Name */}
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 700,
                                      mb: 1.5,
                                      fontSize: '0.95rem',
                                      lineHeight: 1.3,
                                      color: 'white',
                                    }}
                                  >
                                    {diseaseDetails.name}
                                  </Typography>

                                  {/* Description */}
                                  {diseaseDetails.description && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mb: 1.5,
                                        lineHeight: 1.4,
                                        color: 'rgba(255, 255, 255, 0.85)',
                                      }}
                                    >
                                      {diseaseDetails.description}
                                    </Typography>
                                  )}

                                  {/* Clinical Information */}
                                  <Box sx={{ mb: 1.5 }}>
                                    {diseaseDetails.omim && (
                                      <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                        <Box
                                          component="span"
                                          sx={{ fontWeight: 600, color: theme.palette.warning.light }}
                                        >
                                          OMIM:
                                        </Box>{' '}
                                        <Box
                                          component="span"
                                          sx={{ fontFamily: 'fontFamilyMono', fontSize: '0.85rem' }}
                                        >
                                          {diseaseDetails.omim}
                                        </Box>
                                      </Typography>
                                    )}
                                    {diseaseDetails.inheritance && (
                                      <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                        <Box
                                          component="span"
                                          sx={{ fontWeight: 600, color: theme.palette.secondary.light }}
                                        >
                                          Inheritance:
                                        </Box>{' '}
                                        {diseaseDetails.inheritance}
                                      </Typography>
                                    )}
                                    {diseaseDetails.prevalence && (
                                      <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: theme.palette.info.light }}>
                                          Prevalence:
                                        </Box>{' '}
                                        {diseaseDetails.prevalence}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Associated Genes */}
                                  {diseaseDetails.genes.length > 0 && (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          mb: 1,
                                          display: 'block',
                                          color: 'rgba(255, 255, 255, 0.7)',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        Associated Genes:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {diseaseDetails.genes.slice(0, 4).map((gene, geneIndex) => (
                                          <Box
                                            key={geneIndex}
                                            sx={{
                                              px: 1,
                                              py: 0.25,
                                              backgroundColor: alpha(theme.palette.secondary.main, 0.13),
                                              borderRadius: '12px',
                                              border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: theme.palette.secondary.light,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                fontFamily: 'fontFamilyMono',
                                              }}
                                            >
                                              {gene}
                                            </Typography>
                                          </Box>
                                        ))}
                                        {diseaseDetails.genes.length > 4 && (
                                          <Box
                                            sx={{
                                              px: 1,
                                              py: 0.25,
                                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                              borderRadius: '12px',
                                              border: '1px solid rgba(255, 255, 255, 0.2)',
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                              }}
                                            >
                                              +{diseaseDetails.genes.length - 4} more
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Synonyms */}
                                  {diseaseDetails.synonyms.length > 0 && (
                                    <Box sx={{ mb: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          mb: 1,
                                          display: 'block',
                                          color: 'rgba(255, 255, 255, 0.7)',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        Also known as:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {diseaseDetails.synonyms.slice(0, 3).map((synonym, synIndex) => (
                                          <Box
                                            key={synIndex}
                                            sx={{
                                              px: 1,
                                              py: 0.25,
                                              backgroundColor: alpha(theme.palette.info.main, 0.13),
                                              borderRadius: '12px',
                                              border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: theme.palette.info.light,
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                              }}
                                            >
                                              {synonym}
                                            </Typography>
                                          </Box>
                                        ))}
                                        {diseaseDetails.synonyms.length > 3 && (
                                          <Box
                                            sx={{
                                              px: 1,
                                              py: 0.25,
                                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                              borderRadius: '12px',
                                              border: '1px solid rgba(255, 255, 255, 0.2)',
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                              }}
                                            >
                                              +{diseaseDetails.synonyms.length - 3} more
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Category */}
                                  {diseaseDetails.category && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: theme.palette.warning.light,
                                          fontWeight: 600,
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        Category: {diseaseDetails.category}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                              >
                                <ReviewChip
                                  label={String(diseaseDetails.name)}
                                  chipColor="info"
                                  size="small"
                                  sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: `${alpha(theme.palette.info.main, 0.13)}`,
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.19)}`,
                                    },
                                  }}
                                />
                              </motion.div>
                            </Tooltip>
                          )
                        })}
                        {diseases.length > 3 && (
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Additional Diseases
                                </Typography>
                                {diseases.slice(3).map((disease, index) => {
                                  const diseaseDetails = getDiseaseDetails(disease)
                                  return (
                                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                      • {diseaseDetails.name}
                                    </Typography>
                                  )
                                })}
                              </Box>
                            }
                            placement="top"
                            arrow
                          >
                            <ReviewChip
                              label={`+${diseases.length - 3} more`}
                              chipColor="default"
                              size="small"
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )}
                      </FormChipContainer>
                    </FormRow>
                  )}

                  {/* Clinical History and Analysis Guidance */}
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {clinicalHistory && (
                      <Grid item xs={12} md={6}>
                        <Tooltip
                          title={
                            clinicalHistory.length > 150 ? (
                              <TooltipContainer maxWidth="400px">
                                <TooltipSubtitle>Clinical History</TooltipSubtitle>
                                <TooltipDescription>{clinicalHistory}</TooltipDescription>
                              </TooltipContainer>
                            ) : (
                              ''
                            )
                          }
                          placement="top"
                          arrow
                          enterDelay={clinicalHistory.length > 150 ? 500 : 999999}
                        >
                          <motion.div
                            whileHover={{ scale: 1.01 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          >
                            <ClinicalTextBox
                              sx={{
                                cursor: clinicalHistory.length > 150 ? 'pointer' : 'default',
                                transition: 'all 0.2s ease',
                                '&:hover':
                                  clinicalHistory.length > 150
                                    ? {
                                        backgroundColor: theme.palette.action.hover,
                                        borderColor: alpha(theme.palette.primary.main, 0.25),
                                      }
                                    : {},
                              }}
                            >
                              <ClinicalTextLabel>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Clinical History
                                  </Typography>
                                </Box>
                              </ClinicalTextLabel>
                              <ClinicalTextContent>
                                {clinicalHistory.length > 150 ? truncateText(clinicalHistory) : clinicalHistory}
                              </ClinicalTextContent>
                            </ClinicalTextBox>
                          </motion.div>
                        </Tooltip>
                      </Grid>
                    )}

                    {userGuidance && (
                      <Grid item xs={12} md={6}>
                        <Tooltip
                          title={
                            userGuidance.length > 150 ? (
                              <TooltipContainer maxWidth="400px">
                                <TooltipSubtitle>Analysis Guidance</TooltipSubtitle>
                                <TooltipDescription>{userGuidance}</TooltipDescription>
                              </TooltipContainer>
                            ) : (
                              ''
                            )
                          }
                          placement="top"
                          arrow
                          enterDelay={userGuidance.length > 150 ? 500 : 999999}
                        >
                          <motion.div
                            whileHover={{ scale: 1.01 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          >
                            <ClinicalTextBox
                              sx={{
                                cursor: userGuidance.length > 150 ? 'pointer' : 'default',
                                transition: 'all 0.2s ease',
                                '&:hover':
                                  userGuidance.length > 150
                                    ? {
                                        backgroundColor: theme.palette.action.hover,
                                        borderColor: alpha(theme.palette.primary.main, 0.25),
                                      }
                                    : {},
                              }}
                            >
                              <ClinicalTextLabel>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Analysis Guidance
                                  </Typography>
                                </Box>
                              </ClinicalTextLabel>
                              <ClinicalTextContent>
                                {userGuidance.length > 150 ? truncateText(userGuidance) : userGuidance}
                              </ClinicalTextContent>
                            </ClinicalTextBox>
                          </motion.div>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </>
          )}
        </ReviewFormContainer>

        {/* Validation Results */}
        {errors.length > 0 && (
          <ValidationSection severity="error">
            <ValidationHeader severity="error">
              <Warning />
              <Typography>Configuration Errors</Typography>
            </ValidationHeader>
            <ValidationList>
              {errors.map((error, index) => (
                <ValidationItem key={index}>{error}</ValidationItem>
              ))}
            </ValidationList>
          </ValidationSection>
        )}

        {warnings.length > 0 && (
          <ValidationSection severity="warning">
            <ValidationHeader severity="warning">
              <Info />
              <Typography>Recommendations</Typography>
            </ValidationHeader>
            <ValidationList>
              {warnings.map((warning, index) => (
                <ValidationItem key={index}>{warning}</ValidationItem>
              ))}
            </ValidationList>
          </ValidationSection>
        )}

        {/* Submit Section */}
        {renderSubmitSection()}
      </ReviewContainer>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {showConfirmation && (
          <ConfirmationOverlay
            variants={confirmationOverlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={handleCancelDiscard}
          >
            <ConfirmationDialog
              variants={confirmationDialogVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <ConfirmationHeader>
                <ConfirmationTitle>Discard Configuration?</ConfirmationTitle>
                <ConfirmationMessage>
                  All your analysis settings and selections will be lost. This action cannot be undone.
                </ConfirmationMessage>
              </ConfirmationHeader>

              <ConfirmationActions>
                <motion.div variants={confirmationButtonVariants} initial="idle" whileHover="hover" whileTap="tap">
                  <ConfirmationButton variant="secondary" onClick={handleCancelDiscard}>
                    Cancel
                  </ConfirmationButton>
                </motion.div>

                <motion.div variants={confirmationButtonVariants} initial="idle" whileHover="hover" whileTap="tap">
                  <ConfirmationButton variant="primary" onClick={handleConfirmDiscard}>
                    Discard
                  </ConfirmationButton>
                </motion.div>
              </ConfirmationActions>
            </ConfirmationDialog>
          </ConfirmationOverlay>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default StepReview
